import { Navigate, useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./InstallPage.module.scss";

const InstallPage = () => {
  usePageTitle(PAGE_TITLES.installLiveEditor);

  const { id } = useParams();

  if (!id) {
    throw new Error("InstallPage: missing site id in params");
  }

  const { team } = useTeam();

  const liveEditorEnabled = Boolean(
    team?.subscription?.price?.product?.live_edior,
  );

  if (!liveEditorEnabled) {
    return <Navigate to={routes.site.root({ id })} replace />;
  }

  const chromeExtensionUrl =
    "https://chrome.google.com/webstore/detail/alli-ai-live-editor/hflhpeanppbliciljifchpeolmoljlik?hl=en&authuser=0";

  return (
    <div className={styles.page}>
      <div className={styles.mainSection}>
        <div className={styles.headerSection}>
          <h2 className={styles.sectionTitle}>
            Optimize Content For Any Site... Instantly from Your Browser
          </h2>
          <p className={styles.sectionDescription}>
            No more training your team for every new CMS, no more tickets, and
            no waiting for developer. Edit and optimize any page content
            instantly from your Google Chrome browser with the Alli AI Live
            Editor.
          </p>

          <div className={styles.videoContainer}>
            <iframe
              width="644"
              height="362"
              src="https://www.youtube.com/embed/SEGcEJC3xNQ"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <div className={styles.installButtonContainer}>
            <a
              href={chromeExtensionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.installButton}
            >
              Install Live Editor
            </a>
          </div>
        </div>
      </div>

      <div className={styles.howItWorksSection}>
        <h2 className={styles.howItWorksTitle}>How Does It Work?</h2>
      </div>

      <div className={styles.stepsSection}>
        {/* Step 1 */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>
            <div className={styles.number}>1</div>
            <div className={styles.stepName}>Install</div>
          </div>
          <div className={styles.stepContent}>
            <p className={styles.stepDescription}>
              First, just install the Alli AI Live Editor Chrome Extension.{" "}
              <strong>
                <a
                  href={chromeExtensionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  You can download it here.
                </a>
              </strong>{" "}
              Then log in to the Extension using your AlliAI.com username and
              password. Make sure the Live Editor is pinned on your Chrome dash
              for ease of use.
            </p>
          </div>
        </div>

        {/* Step 2 */}
        <div className={`${styles.step} ${styles.stepReverse}`}>
          <div className={styles.stepContent}>
            <p className={styles.stepDescription}>
              Visit any website you've added to your Alli AI dashboard, and{" "}
              <strong>the Alli AI logo icon will light up</strong>. Click it and
              you can view all of your active and recommended Automations, edit
              any page content, and add or edit any code you like as
              Customizations.
            </p>
          </div>
          <div className={styles.stepNumber}>
            <div className={styles.number}>2</div>
            <div className={styles.stepName}>Optimize</div>
          </div>
        </div>

        {/* Step 3 */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>
            <div className={styles.number}>3</div>
            <div className={styles.stepName}>Scale</div>
          </div>
          <div className={styles.stepContent}>
            <p className={styles.stepDescription}>
              That's it!{" "}
              <strong>
                Once you save a change, it will be live within seconds.
              </strong>{" "}
              Make any changes you want to optimize the site, and edit or undo
              any previous Customizations with a few mouse clicks.
            </p>

            <div className={styles.finalInstallContainer}>
              <a
                href={chromeExtensionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.installButton}
              >
                Install Live Editor
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPage;
